import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from './cn';

/*
  Card — поверхность с тонкой рамкой. Корпус для модульных карточек, строк списков,
  секций настроек. Радиус lg (16) — не круглее холста (R8).

  interactive — рецепт `lift`: масштаб от центра (без translate — иначе дрожит у края
  курсора), большая мягкая тень, рамка strong; 480 ms ease-out-expo.
  elevated — статичная тень md.
*/

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
  elevated?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ interactive, elevated, className, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="card"
        className={cn(
          'rounded-lg border border-line bg-card',
          elevated && 'shadow-md',
          interactive && 'lift cursor-pointer focus-within:ring-2 focus-within:ring-ring/30',
          className,
        )}
        {...rest}
      />
    );
  },
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} data-slot="card-header" className={cn('flex flex-col gap-1 p-5', className)} {...rest} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...rest }, ref) => (
    <h3 ref={ref} data-slot="card-title" className={cn('type-subheading text-fg', className)} {...rest} />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...rest }, ref) => (
    <p ref={ref} data-slot="card-description" className={cn('text-xs text-fg-muted', className)} {...rest} />
  ),
);
CardDescription.displayName = 'CardDescription';

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} data-slot="card-body" className={cn('px-5 pb-5', className)} {...rest} />
  ),
);
CardBody.displayName = 'CardBody';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div
      ref={ref}
      data-slot="card-footer"
      className={cn('flex items-center gap-2 border-t border-line px-5 py-3', className)}
      {...rest}
    />
  ),
);
CardFooter.displayName = 'CardFooter';
